import type { CollabCommentData } from '@examdojo/core/tiptap-react-ui-components/lib/collab-config';
import type { TCollabComment } from '@tiptap-pro/provider';
import { useCallback, useEffect, useRef, useState } from 'react';

interface CommentCardProps {
  comment: TCollabComment<CollabCommentData>;
  threadId: string;
  onUpdate?: (threadId: string, commentId: string, content: string) => void;
  onDelete?: (threadId: string, commentId: string) => void;
  onResolve?: () => void;
  isResolved?: boolean;
}

const AVATAR_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#14b8a6'];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function UserAvatar({ name, avatarUrl }: { name: string; avatarUrl?: string }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [avatarUrl]);

  if (avatarUrl && !imgFailed) {
    return (
      <img
        className="threads-panel-comment-avatar"
        src={avatarUrl}
        alt={name}
        onError={() => setImgFailed(true)}
        style={{ objectFit: 'cover' }}
      />
    );
  }

  return (
    <div className="threads-panel-comment-avatar" style={{ background: getAvatarColor(name) }}>
      {getInitials(name)}
    </div>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

export function CommentCard({ comment, threadId, onUpdate, onDelete, onResolve, isResolved }: CommentCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const userName = comment.data?.userName ?? 'Anonymous';
  const userAvatar = comment.data?.userAvatar;

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleStartEdit = useCallback(() => {
    setEditText(typeof comment.content === 'string' ? comment.content : JSON.stringify(comment.content));
    setIsEditing(true);
    setMenuOpen(false);
  }, [comment.content]);

  const handleSaveEdit = useCallback(() => {
    if (editText.trim()) {
      onUpdate?.(threadId, comment.id, editText.trim());
    }
    setIsEditing(false);
  }, [threadId, comment.id, editText, onUpdate]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditText('');
  }, []);

  const handleDelete = useCallback(() => {
    setMenuOpen(false);
    onDelete?.(threadId, comment.id);
  }, [threadId, comment.id, onDelete]);

  const hasOverlay = onUpdate || onDelete || onResolve;

  return (
    <div className="threads-panel-comment">
      <UserAvatar name={userName} avatarUrl={userAvatar} />

      <div className="threads-panel-comment-body">
        <div className="threads-panel-comment-header">
          <span className="threads-panel-comment-author">{userName}</span>
          <span className="threads-panel-comment-time">{formatRelativeTime(comment.createdAt)}</span>
        </div>

        {isEditing ? (
          <div className="threads-panel-comment-edit">
            <textarea
              className="threads-panel-input"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveEdit();
                }
                if (e.key === 'Escape') handleCancelEdit();
              }}
              autoFocus
              rows={2}
            />
            <div className="threads-panel-comment-edit-actions">
              <button type="button" className="threads-panel-btn threads-panel-btn-ghost" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button type="button" className="threads-panel-btn threads-panel-btn-primary" onClick={handleSaveEdit}>
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="threads-panel-comment-content">
            {typeof comment.content === 'string' ? comment.content : JSON.stringify(comment.content)}
          </div>
        )}
      </div>

      {hasOverlay && !isEditing && (
        <div className="threads-panel-comment-overlay">
          {onResolve && (
            <button
              type="button"
              className={`threads-panel-overlay-btn ${isResolved ? 'resolve-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onResolve();
              }}
              title={isResolved ? 'Reopen' : 'Resolve'}
            >
              ✓
            </button>
          )}

          {(onUpdate || onDelete) && (
            <div className="threads-panel-dropdown-anchor" ref={menuRef}>
              <button
                type="button"
                className="threads-panel-overlay-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((prev) => !prev);
                }}
                title="More actions"
              >
                ⋯
              </button>

              {menuOpen && (
                <div className="threads-panel-dropdown">
                  {onUpdate && (
                    <button
                      type="button"
                      className="threads-panel-dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEdit();
                      }}
                    >
                      <span className="threads-panel-dropdown-icon">✏</span>
                      Edit
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      className="threads-panel-dropdown-item danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                      }}
                    >
                      <span className="threads-panel-dropdown-icon">🗑</span>
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
